import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "@/components/ui/inputForm";
import { Button } from "@/components/ui/button";
import { message, Select, ConfigProvider, theme as antdTheme, Checkbox } from "antd";
import { getAllServicos } from "@/services/servico";
import { getEspecificaById, saveEspecifica, getAllEspecificas } from "@/services/especifica"; 

const defaultValues = {
  nome: "",
  valor: "",
  aplicaDescontoTurma: false,
  servicoVinculadoId: undefined,
  especificasRelacionadas: [], // agora é array
  defaultChecked: false,
};

function formatCurrency(value: string | number) {
  if (value === null || value === undefined || value === "") return "";
  const number = typeof value === "number" ? value : Number(value.toString().replace(/\D/g, "")) / 100;
  return number.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function parseCurrency(value: string) {
  if (!value) return 0;
  return Number(value.replace(/[^\d]/g, "")) / 100;
}

export default function CadastroEspecifica() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [servicosMonitoria, setServicosMonitoria] = useState<any[]>([]);
  const [especificasAtivas, setEspecificasAtivas] = useState<any[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  useEffect(() => {
    getAllServicos().then((res) => {
      if (res.status === 200) {
        const monitorias = res.data.filter((s: any) => s.tipo === "MONITORIA");
        setServicosMonitoria(monitorias.map((s: any) => ({ value: s.id, label: s.nome })));
      }
    });
  }, []);

  useEffect(() => {
    getAllEspecificas().then((res) => {
      if (res.status === 200) {
        const ativas = res.data.filter((e: any) => e.ativo);
        setEspecificasAtivas(ativas.map((e: any) => ({ value: e.id.toString(), label: e.nome })));
      }
    });
  }, []);

  useEffect(() => {
    // Só faz o reset quando as opções já estiverem carregadas
    if (isEdit && id && especificasAtivas.length > 0) {
      getEspecificaById(id).then((res) => {
        if (res.status === 200) {
          const especifica = res.data;
          reset({
            ...defaultValues,
            ...especifica,
            valor: formatCurrency(especifica.valor),
            aplicaDescontoTurma: !!especifica.aplicaDescontoTurma,
            servicoVinculadoId: especifica.servicoVinculadoId || undefined,
            especificasRelacionadas: especifica.especificasRelacionadas
              ? especifica.especificasRelacionadas
                  .split(";")
                  .filter(Boolean)
                  .map((id: string | number) => id.toString())
              : [],
            defaultChecked: !!especifica.defaultChecked,
          });
        }
      });
    } else if (!isEdit) {
      reset(defaultValues);
    }
  }, [id, isEdit, reset, especificasAtivas]);

  const onSubmit = async (data: any) => {
    try {
      const payload = {
        ...data,
        id: id || undefined,
        valor: parseCurrency(data.valor),
        aplicaDescontoTurma: !!data.aplicaDescontoTurma,
        servicoVinculadoId: data.servicoVinculadoId || null,
        especificasRelacionadas: Array.isArray(data.especificasRelacionadas)
          ? data.especificasRelacionadas.join(";")
          : data.especificasRelacionadas || "",
        defaultChecked: !!data.defaultChecked,
      };
      const response = await saveEspecifica(payload);
      if (response.status === 200 || response.status === 201) {
        message.success("Específica salva com sucesso!");
        navigate("/especifica");
      }
    } catch (error: any) {
      message.error("Erro ao salvar específica");
    }
  };

  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length < 3) value = value.padStart(3, "0");
    const number = Number(value) / 100;
    setValue("valor", number.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }), { shouldValidate: true });
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-8">
          <span className="text-white text-2xl">{isEdit ? "Editar Específica" : "Cadastro de Específica"}</span>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="col-span-2">
            <label>Nome *</label>
            <Input type="text" placeholder="Nome" {...register("nome", { required: "Nome é obrigatório" })} />
            {errors.nome && <span className="text-red-600">{errors.nome.message}</span>}
          </div>
          <div>
            <label>Valor *</label>
            <Input
              type="text"
              placeholder="R$ 0,00"
              {...register("valor", { required: "Valor é obrigatório" })}
              onChange={handleValorChange}
              value={watch("valor")}
            />
            {errors.valor && <span className="text-red-600">{errors.valor.message}</span>}
          </div>
          <div className="flex items-center mt-6">
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm] }}>
              <Checkbox
                id="aplicaDescontoTurma"
                checked={watch("aplicaDescontoTurma")}
                onChange={(e) => setValue("aplicaDescontoTurma", e.target.checked, { shouldValidate: true })}
              >
                <label htmlFor="aplicaDescontoTurma" className="text-white ml-2">É considerado para desconto?</label>
              </Checkbox>
            </ConfigProvider>
          </div>
          <div className="flex items-center mt-6">
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm] }}>
              <Checkbox
                id="defaultChecked"
                checked={watch("defaultChecked")}
                onChange={(e) => setValue("defaultChecked", e.target.checked, { shouldValidate: true })}
              >
                <label htmlFor="defaultChecked" className="text-white ml-2">Marcado Padrão</label>
              </Checkbox>
            </ConfigProvider>
          </div>
          <div className="col-span-2">
            <label>Serviço Vinculado</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <Select
                className="w-full h-10"
                value={watch("servicoVinculadoId")}
                onChange={value => setValue("servicoVinculadoId", value, { shouldValidate: true })}
                options={servicosMonitoria}
                placeholder="Selecione o serviço vinculado (opcional)"
                allowClear
              />
            </ConfigProvider>
          </div>
          <div className="col-span-2">
            <label>Relacionar Específicas Ativas</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <Select
                mode="multiple"
                className="w-full h-10"
                value={watch("especificasRelacionadas") || []}
                onChange={selected =>
                  setValue(
                    "especificasRelacionadas",
                    selected,
                    { shouldValidate: true }
                  )
                }
                options={especificasAtivas}
                placeholder="Selecione as específicas relacionadas"
                allowClear
              />
            </ConfigProvider>
          </div>
        </div>
        <div className="flex justify-start p-2 mt-12 gap-8">
          <Button
            className="flex gap-2"
            variant="destructive"
            type="button"
            onClick={() => navigate("/especifica")}
          >
            Cancelar
          </Button>
          <Button className="flex gap-2" type="submit">
            Salvar
          </Button>
        </div>
      </form>
    </div>
  );
}